import {
    normalizeSearchIntentQuery,
    type ParsedSearchIntentV2,
} from "@wags/discovery/index.ts";

const rememberedParsedSearchIntents = new Map<string, ParsedSearchIntentV2>();

const STRUCTURED_LISTING_FILTER_KEYS = [
    "breedSlugs",
    "dogName",
    "breedType",
    "providerName",
    "providerType",
    "size",
    "coatType",
    "sheddingLevel",
    "hypoallergenic",
    "priceMin",
    "priceMax",
    "dobMin",
    "dobMax",
    "sex",
    "friendlinessChildren",
    "friendlinessDogs",
    "friendlinessCats",
    "energyLevel",
    "trainability",
    "healthIssuesRisk",
    "bestMatchPreferences",
] as const;

export function listingInterpretationsFromParsed(
    parsed: ParsedSearchIntentV2,
): ParsedSearchIntentV2["intents"] {
    return parsed.intents.filter(
        (interpretation) => interpretation.target === "listings",
    );
}

export function rememberParsedSearchIntent(
    query: string,
    parsed: ParsedSearchIntentV2,
) {
    rememberedParsedSearchIntents.set(
        normalizeSearchIntentQuery(query),
        parsed,
    );
}

export function getRememberedParsedSearchIntent(query: string | undefined) {
    if (!query) return undefined;
    return rememberedParsedSearchIntents.get(normalizeSearchIntentQuery(query));
}

export function clearRememberedParsedSearchIntents() {
    rememberedParsedSearchIntents.clear();
}

export function shouldUseParsedListingSearch<T extends { q?: string }>(
    params: T,
) {
    if (!params.q?.trim()) return false;
    return STRUCTURED_LISTING_FILTER_KEYS.every(
        (key) => Reflect.get(params, key) === undefined,
    );
}

export function withSeekerLocationOnListingIntents(
    parsed: ParsedSearchIntentV2,
    location: {
        lat: number;
        lng: number;
        maxDistanceMiles?: number;
    },
): ParsedSearchIntentV2 {
    if (!Number.isFinite(location.lat) || !Number.isFinite(location.lng)) {
        return parsed;
    }

    return {
        intents: parsed.intents.map((interpretation) => {
            if (interpretation.target !== "listings") return interpretation;
            const filters = interpretation.intent.filters;
            if (filters.coordinates || filters.locationQuery) {
                return interpretation;
            }
            return {
                ...interpretation,
                intent: {
                    ...interpretation.intent,
                    filters: {
                        ...filters,
                        coordinates: {
                            latitude: location.lat,
                            longitude: location.lng,
                        },
                        maxDistanceMiles:
                            location.maxDistanceMiles ??
                            filters.maxDistanceMiles,
                    },
                },
            };
        }),
    };
}
